const ADSENSE_PUBLISHER_ID_FALLBACK =
  process.env.ADSENSE_PUBLISHER_ID ||
  process.env.NEXT_PUBLIC_ADSENSE_PUBLISHER_ID ||
  'ca-pub-4532354705431691';

/**
 * Tiny helpers shared between the AdSense script loader and the AdSlot
 * component. Keeping the publisher-id parsing in one place means typos
 * like `ca-pub-` vs `pub-` get normalized identically everywhere.
 */

/**
 * Pull the numeric publisher id out of any reasonable input shape:
 *   "ca-pub-1234567890123456"  → "pub-1234567890123456"
 *   "pub-1234567890123456"     → "pub-1234567890123456"
 *   "1234567890123456"         → "pub-1234567890123456"
 *   "" / garbage               → null
 */
export function extractAdsensePublisherId(raw: string): string | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;
  const m = trimmed.match(/(?:ca-)?pub-(\d{10,20})/i);
  if (m) return `pub-${m[1]}`;
  if (/^\d{10,20}$/.test(trimmed)) return `pub-${trimmed}`;
  return null;
}

export function resolveAdsensePublisherId(raw: string): string | null {
  return extractAdsensePublisherId(raw) || extractAdsensePublisherId(ADSENSE_PUBLISHER_ID_FALLBACK);
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

/**
 * Push an ad-render request into AdSense's queue. Called by `AdSlot` after
 * mounting an `<ins>` element. AdSense reads `window.adsbygoogle` and
 * processes any pending entries once its script is loaded — pushing before
 * the script lands is fine, the queue persists.
 */
export function pushAdRequest() {
  if (typeof window === 'undefined') return;
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
  } catch {
    /* swallow — first push on a freshly-loaded slot can throw if the script
       hasn't initialized yet. AdSense recovers on the next push. */
  }
}
